import { createLogger } from '@svenskaspelab/nova-core/log';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createStore } from 'zustand/vanilla';

import type { CouponId, CouponIndex } from '../../../../schema/coupon/entry.js';
import type { Definition } from '../../../../schema/frameworks/entry.js';
import { entriesFromObject } from '../../../../schema/utils/entry.js';
import type { CouponState } from '../../../domain/entry.js';
import { persistLocalStorage, stateChanged } from '../../../zustand/entry.js';
import { filterModifiedCoupons } from './utils/filterModifiedCoupons.js';
import { parseCoupons } from './utils/parseCoupons.js';

const logger = createLogger('poolbetting.coupon-repository');

type CouponStateInternal = {
  _hasHydrated: boolean;
};

type CouponActionsInternal = {
  actions: {
    _setHasHydrated: (hasHydrated: boolean) => void;
  };
};

type CouponActions = object;

export type CouponStore = CouponState & CouponActions & CouponStateInternal & CouponActionsInternal;

const storeName = 'CouponStore';

export type CouponStoreMiddlewareStack = [
  // Add custom types to make middlewares work, should be same order:
  // https://github.com/pmndrs/zustand/discussions/1281#discussioncomment-3616851
  // IMPORTANT! The order of the middlewares might be important.
  // Think twice before reorder, adding or deleting new middlewares.
  ['zustand/devtools', never],
  ['zustand/immer', never],
  ['zustand/persist', Pick<CouponState, 'Coupons' | 'ModifiedCoupons' | 'Wallets'>],
  ['zustand/subscribeWithSelector', never],
  ['tipsen/stateChanged', { couponId: CouponId; couponIndex?: CouponIndex }],
];

export type CouponStoreInstance = ReturnType<typeof createStore<CouponStore, CouponStoreMiddlewareStack>>;

/**
 * Creates a Zustand store for tips coupons with the given initial state.
 * The store will be enhanced with the following middlewares:
 * - devtools.
 * - immer.
 * - persist.
 * - subscribeWithSelector.
 *
 * @param moduleEngine The module engine name
 * @param initialState The initial state to hydrate the store with
 * @returns The enhanced created coupon Zustand store
 */
export function createCouponStore(
  moduleEngine: Definition['moduleEngine'],
  initialState?: Pick<CouponState, 'Coupons' | 'Wallets'>
): CouponStoreInstance {
  return createStore<CouponStore, CouponStoreMiddlewareStack>(
    devtools(
      immer(
        persist(
          subscribeWithSelector(
            stateChanged(
              (set) => ({
                Coupons: {},
                Wallets: {},
                ...initialState,
                ModifiedCoupons: {},
                _hasHydrated: false,
                actions: {
                  _setHasHydrated: (hasHydrated: boolean): void => {
                    set({ _hasHydrated: hasHydrated }, undefined, '_setHasHydrated');
                  },
                },
              }),
              {
                actionName: 'couponModified',
                onUpdate: (action, currentState) => {
                  const { ModifiedCoupons: CurrentModifiedCoupons, Coupons } = currentState;
                  const { couponId, couponIndex } = action.args;
                  if (typeof couponIndex !== 'number') {
                    return { ModifiedCoupons: { ...CurrentModifiedCoupons } };
                  }
                  const coupon = Coupons[couponId]?.[couponIndex];

                  if (!coupon) {
                    return { ModifiedCoupons: { ...CurrentModifiedCoupons } };
                  }

                  const isCouponModified = coupon.boards.some((board) => board.outcomes.flat(Infinity).length > 0);

                  if (!isCouponModified) {
                    const updatedCoupons = { ...CurrentModifiedCoupons };
                    updatedCoupons[couponId] =
                      updatedCoupons[couponId]?.filter((_, index) => index !== couponIndex) ?? [];
                    const filteredModifiedCoupons = Object.fromEntries(
                      Object.entries(updatedCoupons).filter(([, couponIndicies]) => couponIndicies.length > 0)
                    );
                    return { ModifiedCoupons: filteredModifiedCoupons };
                  }

                  if (CurrentModifiedCoupons[couponId]?.includes(couponIndex)) {
                    return {
                      ModifiedCoupons: {
                        ...CurrentModifiedCoupons,
                      },
                    };
                  }

                  return {
                    ModifiedCoupons: {
                      ...CurrentModifiedCoupons,
                      [couponId]: [...(CurrentModifiedCoupons[couponId] ?? []), couponIndex],
                    },
                  };
                },
              }
            )
          ),
          {
            name: `${moduleEngine}.${storeName}`,
            version: 0,
            skipHydration: true,
            partialize: (state) => ({
              Coupons: state.Coupons,
              ModifiedCoupons: state.ModifiedCoupons,
              Wallets: state.Wallets,
            }),
            storage: persistLocalStorage<Pick<CouponState, 'Coupons' | 'ModifiedCoupons' | 'Wallets'>>(
              parseCoupons,
              filterModifiedCoupons
            ),
            merge: (persistedState, currentState) => {
              logger.debug('Merging coupon store', { currentState, persistedState });
              const coupons = { ...currentState.Coupons };
              let modifiedCoupons: CouponState['ModifiedCoupons'] = {};
              const wallets: CouponState['Wallets'] = {};

              try {
                // This type cast should be safe but we wrap in in a try-catch to be safe
                const typedPersistedState = persistedState as CouponState;
                modifiedCoupons = typedPersistedState.ModifiedCoupons;

                // Only merge coupons that are already in the store
                // other coupons are likely outdated and should be removed
                for (const [couponId, coupon] of entriesFromObject(typedPersistedState.Coupons)) {
                  if (coupons[couponId]) {
                    coupons[couponId] = coupon;

                    // Only merge wallets with couponIds that are in store, otherwise remove wallets with likely outdated draws.
                    // eslint-disable-next-line max-depth
                    if (typedPersistedState.Wallets[couponId]) {
                      wallets[couponId] = typedPersistedState.Wallets[couponId];
                    }
                  }
                }
              } catch (error: unknown) {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                const err = error instanceof Error ? error : new Error(`${error}`);
                logger.warn('Failed to merge persisted state', { error: err.message });
              }

              const mergedState = {
                ...currentState,
                Coupons: coupons,
                ModifiedCoupons: modifiedCoupons,
                Wallets: wallets,
              };

              logger.debug('Successfully merged coupon store');

              return mergedState;
            },
            onRehydrateStorage: (startState) => {
              logger.debug('Rehydrating coupon store', { startState });

              return (hydratedState, error): void => {
                if (error) {
                  logger.warn('Error rehydrating coupon store', { error });
                  return;
                }

                hydratedState?.actions._setHasHydrated(true);

                logger.debug('Successfully rehydrated coupon store', { hydratedState });
              };
            },
          }
        )
      ),
      {
        enabled: true,
        name: storeName,
        store: storeName,
      }
    )
  );
}
